import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { API_BASE_URL } from '../config/api.config';
import { getBusinessKey } from '../config/tenant.config';
import { AuthUser, LoginResponse, UserRole } from '../models/auth.model';

const STORAGE_KEY = 'ecommerce.auth';

interface StoredAuth {
  token: string;
  user: AuthUser;
  businessKey: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);

  private readonly tokenSignal = signal<string | null>(null);
  private readonly userSignal = signal<AuthUser | null>(null);
  private readonly businessKeySignal = signal<string | null>(null);
  private readonly adminSessionReadySignal = signal(false);

  readonly user = this.userSignal.asReadonly();
  readonly businessKey = this.businessKeySignal.asReadonly();
  readonly adminSessionReady = this.adminSessionReadySignal.asReadonly();
  readonly isLoggedIn = computed(() => this.isTokenFresh(this.tokenSignal()));
  readonly canManageUsers = computed(() => {
    const role = this.userSignal()?.role;
    return role === 'PortalAdmin' || role === 'BusinessAdmin';
  });
  readonly canSwitchBusiness = computed(() => {
    const user = this.userSignal();
    if (!user) {
      return false;
    }
    if (user.role === 'PortalAdmin') {
      return (user.businesses?.length ?? 0) > 1;
    }
    return user.role === 'BusinessAdmin' && (user.businesses?.length ?? 0) > 1;
  });
  readonly canManageModules = computed(() => this.userSignal()?.role === 'PortalAdmin');
  readonly canUseChat = computed(() => this.userSignal()?.role === 'BusinessAdmin');
  readonly isImpersonating = computed(() => !!this.userSignal()?.impersonatorId);

  constructor() {
    this.restore();
    if (!this.businessKeySignal()) {
      this.businessKeySignal.set(getBusinessKey() || null);
    }
  }

  markAdminSessionReady(): void {
    this.adminSessionReadySignal.set(true);
  }

  invalidateAdminSession(): void {
    this.adminSessionReadySignal.set(false);
  }

  login(email: string, password: string): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(`${API_BASE_URL}/auth/login`, { email, password })
      .pipe(tap((res) => this.setSession(res.token, res.user)));
  }

  requestPasswordReset(email: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${API_BASE_URL}/auth/forgot-password`, { email });
  }

  resetPassword(token: string, password: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${API_BASE_URL}/auth/reset-password`, { token, password });
  }

  refreshMe(): Observable<AuthUser> {
    return this.http.get<AuthUser>(`${API_BASE_URL}/auth/me`).pipe(
      tap((user) => {
        const token = this.tokenSignal();
        if (token) {
          this.setSession(token, user);
        }
      })
    );
  }

  impersonate(userId: string): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(`${API_BASE_URL}/auth/impersonate/${userId}`, {})
      .pipe(tap((res) => this.setSession(res.token, res.user)));
  }

  exitImpersonation(): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(`${API_BASE_URL}/auth/impersonate/exit`, {})
      .pipe(tap((res) => this.setSession(res.token, res.user)));
  }

  logout(): void {
    this.tokenSignal.set(null);
    this.userSignal.set(null);
    this.adminSessionReadySignal.set(false);
    this.businessKeySignal.set(getBusinessKey() || null);
    localStorage.removeItem(STORAGE_KEY);
  }

  setBusinessKey(key: string): void {
    const normalized = normalizeBusinessKey(key);
    if (!normalized) {
      return;
    }
    this.businessKeySignal.set(normalized);
    this.persist();
  }

  validToken(): string | null {
    const token = this.tokenSignal();
    return this.isTokenFresh(token) ? token : null;
  }

  private setSession(token: string, user: AuthUser): void {
    this.tokenSignal.set(token);
    this.userSignal.set(this.normalizeUser(user));
    this.ensureBusinessKey(this.userSignal()!);
    this.persist();
  }

  private ensureBusinessKey(user: AuthUser): void {
    const allowed = user.businesses ?? [];
    const persisted = normalizeBusinessKey(this.businessKeySignal());

    // Keep the admin switcher selection across refresh (do not prefer host storefront).
    if (persisted && allowed.some((b) => b.key === persisted)) {
      this.businessKeySignal.set(persisted);
      return;
    }

    if (user.role === 'BusinessAdmin' && allowed.length === 1) {
      this.businessKeySignal.set(allowed[0].key);
      return;
    }

    if (user.role === 'BusinessAdmin' && user.businessKey && allowed.some((b) => b.key === user.businessKey)) {
      const current = this.businessKeySignal();
      if (!current || !allowed.some((b) => b.key === current)) {
        this.businessKeySignal.set(user.businessKey);
      }
      return;
    }

    if (allowed.length === 0) {
      this.businessKeySignal.set(getBusinessKey() || null);
      return;
    }

    if (user.role !== 'PortalAdmin' && allowed.length === 1) {
      this.businessKeySignal.set(allowed[0].key);
      return;
    }

    const storefront = getBusinessKey();
    if (storefront && allowed.some((b) => b.key === storefront)) {
      this.businessKeySignal.set(storefront);
      return;
    }

    this.businessKeySignal.set(allowed[0].key);
  }

  private isTokenFresh(token: string | null): boolean {
    if (!token) {
      return false;
    }

    const exp = decodeJwtExp(token);
    if (exp == null) {
      return false;
    }

    return exp * 1000 > Date.now();
  }

  private restore(): void {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return;
    }

    try {
      const stored = JSON.parse(raw) as StoredAuth;
      if (!stored?.token || !stored.user) {
        return;
      }

      if (!this.isTokenFresh(stored.token)) {
        this.logout();
        return;
      }

      this.tokenSignal.set(stored.token);
      this.userSignal.set(this.normalizeUser(stored.user));
      this.businessKeySignal.set(normalizeBusinessKey(stored.businessKey));
      // Do not reconcile tenant from stale cached user.businesses — refreshMe loads fresh profile.
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
  }

  private persist(): void {
    const token = this.tokenSignal();
    const user = this.userSignal();
    if (!token || !user) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }

    const payload: StoredAuth = {
      token,
      user,
      businessKey: this.businessKeySignal() ?? getBusinessKey() ?? ''
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  }

  private normalizeUser(user: AuthUser): AuthUser {
    return {
      ...user,
      role: user.role as UserRole,
      businesses: user.businesses ?? [],
      impersonatorId: user.impersonatorId ?? null,
      impersonatorName: user.impersonatorName ?? null
    };
  }
}

function normalizeBusinessKey(key: string | null | undefined): string | null {
  const trimmed = (key ?? '').trim().toLowerCase();
  return trimmed || null;
}

function decodeJwtExp(token: string): number | null {
  const parts = token.split('.');
  if (parts.length < 2) {
    return null;
  }

  try {
    const padded = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(padded);
    const payload = JSON.parse(json) as { exp?: number };
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}
